// FilmCarousel.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import gsap from "gsap";

const IMG_W = 500;
const IMG_H = 300;

export default function FilmCarousel({ images = [],  active = false  }) {
  const axisRef = useRef(0); // GSAP animates this directly
  const [axis, setAxis] = useState(0);
  const n = images.length;

  // Wrap float pos to (-n/2, n/2] so every image stays near center
  const wrapPos = useCallback((raw) => {
    let p = ((raw % n) + n) % n;
    if (p > n / 2) p -= n;
    return p;
  }, [n]);

  const go = useCallback((dir) => {
    gsap.killTweensOf(axisRef);
    gsap.to(axisRef, {
      current: Math.round(axisRef.current) + dir,
      duration: 0.85,
      ease: "power2.inOut",
      onUpdate: () => setAxis(axisRef.current),
    });
  }, []);

  // Auto-advance
 useEffect(() => {
  if (!active) return;
  const id = setInterval(() => go(1), 5000);
  return () => clearInterval(id);
}, [go, active]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft")  go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [go]);

  const currentIdx = ((Math.round(axis) % n) + n) % n;

const getStyle = (floatPos) => {
  const abs = Math.abs(floatPos);
  const sphereAngle = floatPos * 158;

  return {
    transform: `translateX(${floatPos * 98}%) translateY(${floatPos * floatPos * 28}%) rotate3d(1, 0.5, 0, ${sphereAngle}deg) scale(${Math.max(0.3, 1.3 - abs * 0.8)})`,
    opacity:   Math.max(0.04, 1 - abs * 0.45),
    filter:    abs < 0.03
      ? "none"
      : `brightness(${Math.max(0.06, 1 - abs * 0.42)}) sepia(${Math.min(0.65, abs * 0.33)})`,
    zIndex: Math.round(20 - abs * 4),
  };
};

  if (!n) return null;

  return (
    <div style={{
      width: "100%", maxWidth: 1200, margin: "0 auto",
      background: "none", 
      borderRadius: 6, overflow: "hidden",
    }}>
      {/* <SprocketStrip /> */}

      <div 
      style={{ 
        position: "relative", 
        height: 420, 
        // marginBottom: "5%",
        overflow: "visible", 
        background: "none" }}>

        {/* Projector beam */}
        <div style={{
          position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
          width: 520, height: "100%",
          background: "radial-gradient(ellipse 50% 30% at 50% 0%, rgb(226, 214, 214) 0%, transparent 0%)",
          pointerEvents: "none", zIndex: 1, 
        }} />

        {/* Perspective wrapper (separate from overflow:hidden) */}
        <div style={{ position: "absolute", inset: 0, perspective: "900px", perspectiveOrigin: "50% 50%" }}>
          <div style={{ position: "absolute", inset: 0, transformStyle: "preserve-3d" }}>
           {images.map((img, i) => {
  const floatPos = wrapPos(i - axis);
  if (Math.abs(floatPos) > 2.6) return null;
  const abs = Math.abs(floatPos);

  // Generate deterministic shards per image
const seed = (i * 7 + 13) % 17;

const shards = [
  // Top-left corner
  { points: `0,0 ${52+seed},0 ${44+seed*2},${22+seed} ${18+seed},${35+seed} 0,${28+seed*2}`,
    dx: abs * -6, dy: abs * -4, rot: abs * -3 },
  // Top-right corner
  { points: `${52+seed},0 100,0 100,${30+seed*2} ${72+seed*2},${38+seed} ${44+seed*2},${22+seed}`,
    dx: abs * 6, dy: abs * -4, rot: abs * 3 },
  // Mid-left
  { points: `0,${28+seed*2} ${18+seed},${35+seed} ${22+seed*2},${65+seed} ${8+seed},${72+seed*2} 0,${60+seed}`,
    dx: abs * -7, dy: abs * 1, rot: abs * -2 },
  // Mid-right
  { points: `100,${30+seed*2} 100,${62+seed} ${88+seed},${70+seed*2} ${68+seed*2},${60+seed} ${72+seed*2},${38+seed}`,
    dx: abs * 7, dy: abs * 1, rot: abs * 2 },
  // Center-left
  { points: `${18+seed},${35+seed} ${44+seed*2},${22+seed} ${50+seed},${55+seed*2} ${28+seed*2},${62+seed} ${22+seed*2},${65+seed}`,
    dx: abs * -3, dy: abs * -2, rot: abs * -1.5 },
  // Center-right
  { points: `${44+seed*2},${22+seed} ${72+seed*2},${38+seed} ${68+seed*2},${60+seed} ${50+seed},${55+seed*2}`,
    dx: abs * 3, dy: abs * -2, rot: abs * 1.5 },
  // Bottom-left corner
  { points: `0,${60+seed} ${8+seed},${72+seed*2} ${22+seed*2},${65+seed} ${28+seed*2},${62+seed} ${18+seed*2},${88+seed} 0,100`,
    dx: abs * -6, dy: abs * 5, rot: abs * -3 },
  // Bottom-right corner
  { points: `${68+seed*2},${60+seed} 100,${62+seed} 100,100 ${24+seed*2},100 ${18+seed*2},${88+seed} ${28+seed*2},${62+seed} ${50+seed},${55+seed*2}`,
    dx: abs * 6, dy: abs * 5, rot: abs * 3 },
  // Bottom-center
  { points: `${18+seed*2},${88+seed} ${24+seed*2},100 0,100 0,${85+seed}`,
    dx: abs * -2, dy: abs * 7, rot: abs * (seed%3 - 1) * 2 },
  // Top splinter
  { points: `${44+seed*2},${22+seed} ${52+seed},0 ${60+seed*2},0 ${56+seed},${30+seed}`,
    dx: abs * (seed%2===0 ? 5 : -5), dy: abs * -7, rot: abs * (seed%4 - 2) * 2 },
];
  return (
    <div
      key={i}
      onClick={() => abs > 0.3 && go(Math.sign(floatPos))}
      style={{
        position: "absolute",
        top: "50%", left: "50%",
        width: IMG_W, height: IMG_H,
        marginLeft: -IMG_W / 2, marginTop: -IMG_H / 2,
        cursor: abs > 0.3 ? "pointer" : "default",
        ...getStyle(floatPos),
      }}
    >
      {shards.map((shard, si) => (
        <div
          key={si}
          style={{
            position: "absolute",
               borderRadius: "30px",
              border: '3px solid rgba(92, 9, 9, 0.9)',
              boxShadow: `
              0 2px 0 rgba(255, 255, 255, 0.03) inset,
              0 -2px 0 rgba(0, 0, 0, 0.5) inset,
              0 12px 24px rgba(0, 0, 0, 0.6),
              0 32px 64px rgba(0, 0, 0, 0.5),
              0 64px 100px rgba(0, 0, 0, 0.35)
            `,
            overflow: 'hidden',     // ← clips video to div shape
              maskImage: `linear-gradient(to right, transparent, white 15%, white 85%, transparent), 
                  linear-gradient(to bottom, transparent, white 10%, white 90%, transparent)`,
            WebkitMaskImage: `linear-gradient(to right, transparent, white 15%, white 85%, transparent), 
                              linear-gradient(to bottom, transparent, white 10%, white 90%, transparent)`,
            height: '100%',
            inset: 0,
            transform: `translate(${shard.dx}px, ${shard.dy}px) rotate(${shard.rot}deg)`,
            transition: "transform 0.6s ease",
            clipPath: `polygon(${shard.points.split(" ").map(p => {
              const [x, y] = p.split(",");
              return `${x}% ${y}%`;
            }).join(", ")})`,
          }}
        >
          <img
            src={img.src}
            alt={img.caption || `Still ${i + 1}`}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%", height: "100%",
              objectFit: "cover",
              display: "block",
              borderRadius: 1,
              pointerEvents: "none",

            }}
          />
          {/* Glass edge highlight */}
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%, rgba(0,0,0,0.2) 100%)",
            clipPath: "inherit",
            pointerEvents: "none",

          }} />
        </div>
      ))}
    </div>
  );
})}
          </div>
        </div>

        {/* Nav arrows */}
   {[
  [-1, "V", "left"],
  [1, "V", "right"]
].map(([dir, label, side]) => (
  <button
    key={side}
    onClick={() => go(dir)}
    className="hover-brighten-pitch"
    style={{
      position: "absolute",
      [side]: 350,
      top: "100%",
      background: "rgba(116,12,12,0.35)",
      border: "1px solid rgba(21, 19, 19, 0.1)",
       boxShadow: `
                          0 2px 0 rgba(255, 255, 255, 0.12) inset,
                          0 -1px 0 rgba(0, 0, 0, 0.45) inset,
                          0 6px 12px rgba(0, 0, 0, 0.5),
                          0 12px 24px rgba(0, 0, 0, 0.35)
                        `,
      color: "rgba(255,255,255,0.75)",
      width: 56,
      height: 56,
      borderRadius: "50%",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
      padding: 2,
    }}
  >
    <span
      style={{
        fontFamily: "'Buzzer', sans-serif",
        fontSize: 58,
        lineHeight: 1,
        transform: side === "left"
          ? "rotate(80deg)"
           : "rotate(-80deg) scaleX(-1)",
        display: "inline-block",
      }}
    >
      {label}
    </span>
  </button>
))}
      </div>

      {/* Info bar */}
      <div style={{
        background: "none", padding: "8px 16px", marginTop: 65, 
        display: "flex", justifyContent: "space-between", alignItems: "center",
        // borderTop: "1px solid rgba(255,255,255,0.04)",
      }}>
        <div>
              <h11 style={{
             borderBottom: '3px solid rgba(92, 9, 9, 0.9)',
          letterSpacing: "0.12em",
          textTransform: "uppercase", flex: 1,
        }}>
          {images[currentIdx]?.caption || ""}
        </h11>
        </div>
      </div>
    </div>
  );
}